import { cn } from "@/lib/utils";
import { Logo } from "components/core/Logo";
import { ChevronDownIcon, DotIcon, Tally1Icon } from "lucide-react";
import { useRouter } from "next/router";
import {
    AnchorHTMLAttributes,
    ClassAttributes,
    ComponentProps,
    HTMLAttributes,
    useState
} from "react";

import { IconBox } from "../IconBox";
import { Text } from "../Text";

const FOOTER_INFO = {
    company: "(주)팀민트",
    ceo: "대표 : 한만형",
    businessNumber: "사업자등록번호 : 417-88-02499",
    salesNumber: "통신판매업신고 : 2023-서울강남-05317",
    address: "06092 서울특별시 강남구 선릉로 648, 세림테크빌딩 6층",
    phone: "대표번호 : 070-4327-5067",
    copyright: "© 2022 teammint, inc. All Rights Reserved.",
    copyrightShortVer: "© teammint Corp.",
    instagram: "https://www.instagram.com/mix.day_/",
    linkedin:
        "https://kr.linkedin.com/company/mix-day?trk=public_post_feed-actor-name"
};

const SNSLink = (
    props: Omit<ComponentProps<typeof Text>, "variant"> &
        ClassAttributes<HTMLAnchorElement> &
        Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "color">
) => <Text variant="button.medium" tag="a" target="_blank" {...props} />;

export const Footer = ({
    className,
    ...props
}: ClassAttributes<HTMLElement> & HTMLAttributes<HTMLElement>) => {
    const router = useRouter();
    const isOriginalContentPage = router.asPath.startsWith(
        "/contents/original/"
    );
    const isPaymentsPage = router.asPath.startsWith("/payments/");
    const isPaymentsFormPage = router.asPath.startsWith("/payments/form/");

    return (
        <footer
            className={cn(
                "relative box-border flex w-full items-start justify-between border-0 border-t-[1.5px] border-solid border-[hsl(var(--border))] px-[var(--page-padding)] py-12",
                className
            )}
            {...props}
        >
            <div className="flex w-full items-start justify-between">
                <div className="flex items-start gap-16">
                    <Logo />
                    <ul className="flex flex-col gap-1 font-['Pretendard'] text-[13px] font-normal leading-[140%] text-text-secondary">
                        <li>{`${FOOTER_INFO.company} | ${FOOTER_INFO.ceo} | ${FOOTER_INFO.businessNumber}`}</li>
                        <li>{`${FOOTER_INFO.businessNumber} | ${FOOTER_INFO.salesNumber}`}</li>
                        <li>{`${FOOTER_INFO.address} | ${FOOTER_INFO.phone}`}</li>
                    </ul>
                </div>
                <div className="flex items-center gap-2">
                    <SNSLink
                        className="px-[10px] py-1"
                        href={FOOTER_INFO.instagram}
                    >
                        Instagram
                    </SNSLink>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="2"
                        height="12"
                        viewBox="0 0 2 12"
                        fill="none"
                    >
                        <path d="M1 0V12" stroke="#BDBDBD" />
                    </svg>
                    <SNSLink
                        className="px-[10px] py-1"
                        href={FOOTER_INFO.linkedin}
                    >
                        Linkedin
                    </SNSLink>
                </div>
            </div>
        </footer>
    );
};

export const SideFooter = ({
    className,
    ...props
}: ClassAttributes<HTMLElement> & HTMLAttributes<HTMLElement>) => {
    const router = useRouter();
    const [open, setOpen] = useState(false);
    const isOriginalContentPage = router.asPath.startsWith(
        "/contents/original/"
    );
    const isPaymentsPage = router.asPath.startsWith("/payments/");
    const isPaymentsFormPage = router.asPath.startsWith("/payments/form/");

    return (
        <footer className={cn("w-full", className)} {...props}>
            <div className="flex flex-col">
                <div className="flex items-center">
                    <SNSLink href={FOOTER_INFO.instagram} className="mr-1">
                        Instagram
                    </SNSLink>
                    <Tally1Icon color="#BDBDBD" size={16} />
                    <SNSLink href={FOOTER_INFO.linkedin} className="-ml-1">
                        Linkedin
                    </SNSLink>
                </div>
                <div className="flex flex-col">
                    <div
                        className="flex cursor-pointer items-center gap-0.5"
                        onClick={() => setOpen((prev) => !prev)}
                    >
                        <Text variant="button.small" color="text.disabled">
                            {FOOTER_INFO.company} 사업자 정보
                        </Text>
                        <ChevronDownIcon
                            color="#BDBDBD"
                            size={16}
                            className={cn(open && "rotate-180")}
                        />
                    </div>
                    <ul className={cn("flex flex-col", !open && "hidden")}>
                        <Text
                            variant="button.small"
                            color="text.disabled"
                            tag="li"
                        >
                            {FOOTER_INFO.ceo}
                        </Text>
                        <Text
                            variant="button.small"
                            color="text.disabled"
                            tag="li"
                        >
                            {FOOTER_INFO.businessNumber}
                        </Text>
                        <Text
                            variant="button.small"
                            color="text.disabled"
                            tag="li"
                        >
                            {FOOTER_INFO.salesNumber}
                        </Text>
                        <Text
                            variant="button.small"
                            color="text.disabled"
                            tag="li"
                        >
                            {FOOTER_INFO.address}
                        </Text>
                        <Text
                            variant="button.small"
                            color="text.disabled"
                            tag="li"
                        >
                            {FOOTER_INFO.phone}
                        </Text>
                    </ul>
                    <Text variant="caption1" color="text.disabled">
                        {FOOTER_INFO.copyrightShortVer}
                    </Text>
                </div>
            </div>
        </footer>
    );
};
